/**
 * Live-updates the header wishlist count from Swish (Wishlist King).
 *
 * Preferred setup (official): paste assets/swish-code-access-header-link.js.txt
 * into Swish admin → Code Access → "Headless Wishlist Link", and enable the
 * Code Access app embed. That path uses Swish's headless component.
 *
 * This script is a theme-side fallback that binds when window.WishlistKing
 * is available, so the counter can still work without Code Access.
 */
(() => {
  const ROOT_SELECTOR = ".wishlist-header-link";
  const LINK_SELECTOR = ".wishlist-header-link .wkh-button";
  const COUNTER_SELECTOR = ".wkh-counter";
  const WISHLIST_EVENTS = [
    "wk:wishlist:add:success",
    "wk:wishlist:remove:success",
    "wk:wishlist:clear:success",
    "wk:wishlist:add-to-cart:success",
    "wk:wishlist:add-all-to-cart:success",
    "wk:wishlist:change-variant:success",
    "wk:customer:login",
    "wk:customer:logout",
  ];

  function getWishlistCount(app) {
    if (!app) return 0;

    if (app.wishlist && typeof app.wishlist.numItems === "number") {
      return app.wishlist.numItems;
    }

    if (typeof app.getWishlist === "function") {
      try {
        const wishlist = app.getWishlist();
        if (wishlist && typeof wishlist.numItems === "number") return wishlist.numItems;
        if (wishlist && Array.isArray(wishlist.items)) return wishlist.items.length;
      } catch (error) {
        // Ignore SDK shape differences across Swish versions.
      }
    }

    const stateWishlist = app.state?.wishlist || app.store?.wishlist || app.data?.wishlist;
    if (stateWishlist && typeof stateWishlist.numItems === "number") {
      return stateWishlist.numItems;
    }

    return 0;
  }

  function updateCounters(numItems) {
    const count = Number(numItems) || 0;

    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const link = root.querySelector(".wkh-button") || root.querySelector("a");
      const counter = root.querySelector(COUNTER_SELECTOR);
      if (!link || !counter) return;

      counter.textContent = String(count);
      link.classList.toggle("wkh-selected", count > 0);
    });
  }

  function bindApp(app) {
    if (!app || app.__grafLantzHeaderCounterBound) return false;
    if (!document.querySelector(LINK_SELECTOR)) return false;

    app.__grafLantzHeaderCounterBound = true;
    updateCounters(getWishlistCount(app));

    const refresh = () => {
      // Let Swish commit wishlist state before we read it.
      requestAnimationFrame(() => updateCounters(getWishlistCount(app)));
    };

    if (app.events && typeof app.events.subscribe === "function") {
      WISHLIST_EVENTS.forEach((eventName) => {
        app.events.subscribe(eventName, (event) => {
          const fromEvent = event?.data?.wishlist?.numItems;
          if (typeof fromEvent === "number") {
            updateCounters(fromEvent);
            return;
          }
          refresh();
        });
      });
    }

    // Some Swish builds also emit DOM CustomEvents.
    WISHLIST_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, refresh);
    });

    return true;
  }

  function init() {
    if (bindApp(window.WishlistKing)) return;

    document.addEventListener(
      "wk:app:created",
      () => {
        bindApp(window.WishlistKing);
      },
      { once: true }
    );

    // Late-loading Swish embed: retry briefly after paint.
    let attempts = 0;
    const poll = window.setInterval(() => {
      attempts += 1;
      if (bindApp(window.WishlistKing) || attempts >= 40) {
        window.clearInterval(poll);
      }
    }, 250);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
